/**
 * MissionMap — ArduPilot-inspired interactive Leaflet map for mission planning.
 *
 * Modes
 * ─────
 *  single    (default) — click once to place target point, right-click for action.
 *  waypoints           — click to add sequential numbered waypoints (WP1→WP2→…),
 *                        polyline route, per-waypoint action + altitude via right-click.
 *  zone                — click to add polygon vertices defining a coverage area
 *                        (scan zone, spray zone, patrol area, etc.). ≥3 pts → filled polygon.
 *
 * All modes
 * ─────────
 *  • Satellite / street tile toggle (top-right)
 *  • Optional live drone position marker (pulsing ✈ icon)
 *  • readOnly flag for display-only rendering
 *  • Distance / area summary strip (bottom)
 */
import { useEffect, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import './MissionMap.css'

import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon   from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl:       markerIcon,
  shadowUrl:     markerShadow,
})

// ─── Public types ──────────────────────────────────────────────────────────────
export type MissionAction = 'land' | 'takeoff' | 'drop_delivery' | 'scan' | 'spray'

export interface SelectedPoint {
  lat: number
  lng: number
  actionType: MissionAction
}

export interface WaypointData {
  lat: number
  lng: number
  altitude?: number
  action?: MissionAction
}

// ─── Internal ──────────────────────────────────────────────────────────────────
interface CtxMenu {
  x: number; y: number
  lat: number; lng: number
  wpIndex?: number
}

type PlanningMode = 'single' | 'waypoints' | 'zone'

export interface MissionMapProps {
  mode?:          PlanningMode
  dronePosition?: { lat: number; lng: number }
  readOnly?:      boolean
  zoneType?:      MissionAction          // zone mode: controls fill/border colour
  // single mode
  initialPoint?:     { lat: number; lng: number; actionType?: MissionAction }
  onLocationSelect?: (pt: SelectedPoint) => void
  // waypoints + zone mode
  initialWaypoints?:   WaypointData[]
  onWaypointsChange?:  (wps: WaypointData[]) => void
}

// ─── Constants ─────────────────────────────────────────────────────────────────
const ACTION_ITEMS: { type: MissionAction; label: string; icon: string; color: string }[] = [
  { type: 'takeoff',       label: 'המראה',       icon: '🛫', color: '#4ade80' },
  { type: 'land',          label: 'נחיתה',       icon: '🛬', color: '#f87171' },
  { type: 'drop_delivery', label: 'הורדת משלוח', icon: '📦', color: '#fbbf24' },
  { type: 'scan',          label: 'סריקה',       icon: '🔍', color: '#a78bfa' },
  { type: 'spray',         label: 'ריסוס',       icon: '💧', color: '#34d399' },
]
const ACTION_LABEL: Record<string, string> = {
  land: 'נחיתה', takeoff: 'המראה', drop_delivery: 'הורדת משלוח', scan: 'סריקה', spray: 'ריסוס',
}
const ACTION_ICON: Record<string, string> = {
  land: '🛬', takeoff: '🛫', drop_delivery: '📦', scan: '🔍', spray: '💧',
}
const ACTION_COLOR: Record<string, string> = {
  land: '#f87171', takeoff: '#4ade80', drop_delivery: '#fbbf24', scan: '#a78bfa', spray: '#34d399',
}

const DEFAULT_CENTER: [number, number] = [31.5, 34.9]
const DEFAULT_ZOOM   = 8
const STREET_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
const SAT_URL    = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'

// ─── Helpers ───────────────────────────────────────────────────────────────────
function haversineM(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000
  const φ1 = lat1 * Math.PI / 180, φ2 = lat2 * Math.PI / 180
  const Δφ = (lat2 - lat1) * Math.PI / 180
  const Δλ = (lng2 - lng1) * Math.PI / 180
  const a  = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

function totalRouteM(wps: WaypointData[]): number {
  let d = 0
  for (let i = 1; i < wps.length; i++) d += haversineM(wps[i-1].lat, wps[i-1].lng, wps[i].lat, wps[i].lng)
  return d
}

/** Approximate polygon area in m² via shoelace on projected coords. */
function polygonAreaM2(wps: WaypointData[]): number {
  if (wps.length < 3) return 0
  const R = 6371000
  let area = 0
  for (let i = 0; i < wps.length; i++) {
    const j  = (i + 1) % wps.length
    const xi = wps[i].lng * Math.PI / 180 * R * Math.cos(wps[i].lat * Math.PI / 180)
    const yi = wps[i].lat * Math.PI / 180 * R
    const xj = wps[j].lng * Math.PI / 180 * R * Math.cos(wps[j].lat * Math.PI / 180)
    const yj = wps[j].lat * Math.PI / 180 * R
    area += xi * yj - xj * yi
  }
  return Math.abs(area / 2)
}

// ─── Component ─────────────────────────────────────────────────────────────────
export default function MissionMap({
  mode            = 'single',
  dronePosition,
  readOnly        = false,
  zoneType,
  initialPoint,
  onLocationSelect,
  initialWaypoints,
  onWaypointsChange,
}: MissionMapProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapRef          = useRef<L.Map | null>(null)
  const tileLayerRef    = useRef<L.TileLayer | null>(null)
  const targetMarkerRef = useRef<L.Marker | null>(null)
  const droneMarkerRef  = useRef<L.Marker | null>(null)
  const wpLayerRef      = useRef<L.LayerGroup | null>(null)
  const polylineRef     = useRef<L.Polyline | null>(null)
  const zonePolygonRef  = useRef<L.Polygon | null>(null)
  // Stale-closure mirror: always current without re-registering handlers
  const wpsRef = useRef<WaypointData[]>([])

  const [satellite,  setSatellite]  = useState(false)
  const [ctxMenu,    setCtxMenu]    = useState<CtxMenu | null>(null)
  const [altPopup,   setAltPopup]   = useState<{ index: number } | null>(null)
  const [altVal,     setAltVal]     = useState('50')
  const [selectedPt, setSelectedPt] = useState<SelectedPoint | null>(
    initialPoint?.actionType
      ? { lat: initialPoint.lat, lng: initialPoint.lng, actionType: initialPoint.actionType }
      : null
  )
  const [waypoints, setWaypoints] = useState<WaypointData[]>(initialWaypoints ?? [])

  wpsRef.current = waypoints

  // ── Tile toggle
  useEffect(() => {
    tileLayerRef.current?.setUrl(satellite ? SAT_URL : STREET_URL)
  }, [satellite])

  // ── Notify parent
  useEffect(() => {
    if (mode === 'waypoints' || mode === 'zone') onWaypointsChange?.(waypoints)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waypoints])

  // ── Sync waypoints → numbered markers + polyline  (waypoints mode only)
  useEffect(() => {
    if (!mapRef.current || !wpLayerRef.current || mode !== 'waypoints') return
    wpLayerRef.current.clearLayers()

    waypoints.forEach((wp, idx) => {
      const num   = idx + 1
      const color = wp.action ? (ACTION_COLOR[wp.action] ?? '#38bdf8') : '#38bdf8'
      const label = wp.action ? ACTION_ICON[wp.action] : `${num}`
      const icon  = L.divIcon({
        className: '',
        html: `<div class="wp-marker" style="--wp-color:${color}" dir="ltr">${label}</div>`,
        iconSize: [28, 28], iconAnchor: [14, 14],
      })

      const marker = L.marker([wp.lat, wp.lng], { icon, draggable: !readOnly, zIndexOffset: 200 })
      const tip = `WP${num}${wp.altitude != null ? ` · ${wp.altitude}m` : ''}${wp.action ? ` · ${ACTION_LABEL[wp.action]}` : ''}`
      marker.bindTooltip(tip, { permanent: false, direction: 'top', offset: [0, -12] })

      if (!readOnly) {
        marker.on('contextmenu', (e: L.LeafletMouseEvent) => {
          e.originalEvent.preventDefault()
          const cp = mapRef.current!.latLngToContainerPoint([wp.lat, wp.lng])
          setCtxMenu({ x: cp.x, y: cp.y, lat: wp.lat, lng: wp.lng, wpIndex: idx })
        })
        marker.on('dragend', () => {
          const pos = marker.getLatLng()
          setWaypoints(prev => prev.map((w, i) => i === idx ? { ...w, lat: pos.lat, lng: pos.lng } : w))
        })
      }
      wpLayerRef.current!.addLayer(marker)
    })

    polylineRef.current?.setLatLngs(waypoints.map(wp => [wp.lat, wp.lng] as L.LatLngTuple))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waypoints, readOnly])

  // ── Sync zone vertices → polygon + vertex markers  (zone mode only)
  useEffect(() => {
    if (!mapRef.current || !wpLayerRef.current || mode !== 'zone') return

    // Zone uses polygon — hide the polyline
    polylineRef.current?.setLatLngs([])

    // Redraw vertex markers
    wpLayerRef.current.clearLayers()
    const zColor = zoneType ? (ACTION_COLOR[zoneType] ?? '#38bdf8') : '#38bdf8'

    waypoints.forEach((wp, idx) => {
      const icon = L.divIcon({
        className: '',
        html: `<div class="zone-vertex" style="--wp-color:${zColor}">${idx + 1}</div>`,
        iconSize: [22, 22], iconAnchor: [11, 11],
      })
      const marker = L.marker([wp.lat, wp.lng], { icon, draggable: !readOnly, zIndexOffset: 300 })
      marker.bindTooltip(`V${idx + 1}`, { permanent: false, direction: 'top', offset: [0, -10] })

      if (!readOnly) {
        marker.on('contextmenu', (e: L.LeafletMouseEvent) => {
          e.originalEvent.preventDefault()
          const cp = mapRef.current!.latLngToContainerPoint([wp.lat, wp.lng])
          setCtxMenu({ x: cp.x, y: cp.y, lat: wp.lat, lng: wp.lng, wpIndex: idx })
        })
        marker.on('dragend', () => {
          const pos = marker.getLatLng()
          setWaypoints(prev => prev.map((w, i) => i === idx ? { ...w, lat: pos.lat, lng: pos.lng } : w))
        })
      }
      wpLayerRef.current!.addLayer(marker)
    })

    // Remove old polygon and redraw
    if (zonePolygonRef.current) {
      zonePolygonRef.current.remove()
      zonePolygonRef.current = null
    }
    if (waypoints.length >= 3) {
      zonePolygonRef.current = L.polygon(
        waypoints.map(w => [w.lat, w.lng] as L.LatLngTuple),
        { color: zColor, weight: 2.5, dashArray: '6 5', fillColor: zColor, fillOpacity: 0.18, interactive: false }
      ).addTo(mapRef.current!)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waypoints, readOnly, zoneType])

  // ── Map init (once)
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return

    const center: [number, number] =
      dronePosition              ? [dronePosition.lat, dronePosition.lng]
      : initialPoint             ? [initialPoint.lat, initialPoint.lng]
      : initialWaypoints?.length ? [initialWaypoints[0].lat, initialWaypoints[0].lng]
      : DEFAULT_CENTER
    const zoom = (dronePosition || initialPoint || initialWaypoints?.length) ? 13 : DEFAULT_ZOOM

    const map = L.map(mapContainerRef.current, { center, zoom, zoomControl: true })
    tileLayerRef.current = L.tileLayer(STREET_URL, {
      attribution: '© <a href="https://openstreetmap.org">OpenStreetMap</a>', maxZoom: 19,
    }).addTo(map)
    wpLayerRef.current  = L.layerGroup().addTo(map)
    polylineRef.current = L.polyline([], {
      color: '#38bdf8', weight: 2.5, opacity: 0.85, dashArray: '6 6',
    }).addTo(map)
    mapRef.current = map

    // Live drone marker
    if (dronePosition) {
      const di = L.divIcon({
        className: '',
        html: '<div class="mission-drone-icon">✈</div>',
        iconSize: [36, 36], iconAnchor: [18, 18],
      })
      droneMarkerRef.current = L.marker([dronePosition.lat, dronePosition.lng], { icon: di })
        .addTo(map).bindPopup('<b>רחפן</b>')
    }

    // ── Mode-specific init
    if (mode === 'single') {
      if (initialPoint) _placeTarget(map, initialPoint.lat, initialPoint.lng)
      if (!readOnly) {
        map.on('click', (e: L.LeafletMouseEvent) => {
          setCtxMenu(null)
          _placeTarget(map, e.latlng.lat, e.latlng.lng)
          setSelectedPt(prev => prev ? { ...prev, lat: e.latlng.lat, lng: e.latlng.lng } : null)
        })
        map.on('contextmenu', (e: L.LeafletMouseEvent) => {
          e.originalEvent.preventDefault()
          _placeTarget(map, e.latlng.lat, e.latlng.lng)
          const cp = map.latLngToContainerPoint(e.latlng)
          setCtxMenu({ x: cp.x, y: cp.y, lat: e.latlng.lat, lng: e.latlng.lng })
        })
      }

    } else if (mode === 'waypoints') {
      if (readOnly && initialWaypoints && initialWaypoints.length > 1) {
        const b = L.latLngBounds(initialWaypoints.map(w => [w.lat, w.lng] as L.LatLngTuple))
        setTimeout(() => map.fitBounds(b, { padding: [48, 48] }), 120)
      }
      if (!readOnly) {
        map.on('click', (e: L.LeafletMouseEvent) => {
          setCtxMenu(null)
          setWaypoints(prev => [...prev, { lat: e.latlng.lat, lng: e.latlng.lng }])
        })
        map.on('contextmenu', (e: L.LeafletMouseEvent) => {
          e.originalEvent.preventDefault()
          const newIdx = wpsRef.current.length
          setWaypoints(prev => [...prev, { lat: e.latlng.lat, lng: e.latlng.lng }])
          const cp = map.latLngToContainerPoint(e.latlng)
          setCtxMenu({ x: cp.x, y: cp.y, lat: e.latlng.lat, lng: e.latlng.lng, wpIndex: newIdx })
        })
      }

    } else if (mode === 'zone') {
      if (readOnly && initialWaypoints && initialWaypoints.length >= 3) {
        const b = L.latLngBounds(initialWaypoints.map(w => [w.lat, w.lng] as L.LatLngTuple))
        setTimeout(() => map.fitBounds(b, { padding: [60, 60] }), 120)
      }
      if (!readOnly) {
        map.on('click', (e: L.LeafletMouseEvent) => {
          setCtxMenu(null)
          setWaypoints(prev => [...prev, { lat: e.latlng.lat, lng: e.latlng.lng }])
        })
        map.on('contextmenu', (e: L.LeafletMouseEvent) => {
          e.originalEvent.preventDefault()
          // Right-click on map: add vertex (no action menu in zone mode)
          setWaypoints(prev => [...prev, { lat: e.latlng.lat, lng: e.latlng.lng }])
        })
      }
    }

    return () => {
      if (zonePolygonRef.current) { zonePolygonRef.current.remove(); zonePolygonRef.current = null }
      map.remove()
      mapRef.current = tileLayerRef.current = targetMarkerRef.current =
        droneMarkerRef.current = wpLayerRef.current = polylineRef.current = null
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Update drone marker position
  useEffect(() => {
    if (droneMarkerRef.current && dronePosition) {
      droneMarkerRef.current.setLatLng([dronePosition.lat, dronePosition.lng])
    }
  }, [dronePosition])

  function _placeTarget(map: L.Map, lat: number, lng: number) {
    if (targetMarkerRef.current) {
      targetMarkerRef.current.setLatLng([lat, lng])
    } else {
      const marker = L.marker([lat, lng], { draggable: !readOnly })
        .addTo(map).bindPopup('<b>נקודת יעד</b><br>לחץ ימני לבחירת פעולה')
      marker.on('contextmenu', (e: L.LeafletMouseEvent) => {
        if (readOnly) return
        e.originalEvent.preventDefault()
        const pos = marker.getLatLng()
        const cp  = map.latLngToContainerPoint([pos.lat, pos.lng])
        setCtxMenu({ x: cp.x, y: cp.y, lat: pos.lat, lng: pos.lng })
      })
      if (!readOnly) {
        marker.on('dragend', () => {
          const pos = marker.getLatLng()
          setSelectedPt(prev => prev ? { ...prev, lat: pos.lat, lng: pos.lng } : null)
        })
      }
      targetMarkerRef.current = marker
    }
  }

  const pickAction = (action: MissionAction) => {
    if (!ctxMenu) return
    if (mode === 'waypoints' && ctxMenu.wpIndex !== undefined) {
      setWaypoints(prev => prev.map((w, i) => i === ctxMenu.wpIndex ? { ...w, action } : w))
    } else {
      const pt: SelectedPoint = { lat: ctxMenu.lat, lng: ctxMenu.lng, actionType: action }
      setSelectedPt(pt)
      targetMarkerRef.current?.bindPopup(
        `<b>נקודת יעד</b><br>${ACTION_ICON[action]} ${ACTION_LABEL[action]}`
      ).openPopup()
      onLocationSelect?.(pt)
    }
    setCtxMenu(null)
  }

  const removeWp = (idx: number) => {
    setWaypoints(prev => prev.filter((_, i) => i !== idx))
    setCtxMenu(null)
  }

  const confirmAlt = () => {
    if (!altPopup) return
    const v = parseFloat(altVal)
    if (!isNaN(v) && v >= 0) {
      setWaypoints(prev => prev.map((w, i) => i === altPopup.index ? { ...w, altitude: v } : w))
    }
    setAltPopup(null)
  }

  const dist = totalRouteM(waypoints)
  const area = polygonAreaM2(waypoints)

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <div
      className="mission-map-wrap"
      onClick={() => { setCtxMenu(null); setAltPopup(null) }}
      onContextMenu={e => e.preventDefault()}
    >
      <div ref={mapContainerRef} className="mission-map-canvas" />

      {/* ── Satellite toggle */}
      <button
        className="map-sat-toggle"
        onClick={e => { e.stopPropagation(); setSatellite(v => !v) }}
        title={satellite ? 'מפת רחובות' : 'תצלום לוויין'}
      >
        {satellite ? '🗺' : '🛰'}
      </button>

      {/* ── Clear all (waypoints + zone mode) */}
      {(mode === 'waypoints' || mode === 'zone') && !readOnly && waypoints.length > 0 && (
        <button
          className="map-clear-btn"
          onClick={e => { e.stopPropagation(); setWaypoints([]) }}
        >
          ✕ נקה הכל
        </button>
      )}

      {/* ── Context menu */}
      {ctxMenu && !readOnly && (
        <div
          className="mission-context-menu"
          ref={el => { if (el) { L.DomEvent.disableClickPropagation(el); L.DomEvent.disableScrollPropagation(el); } }}
          style={{ left: ctxMenu.x, top: ctxMenu.y }}
          onClick={e => e.stopPropagation()}
          onContextMenu={e => e.stopPropagation()}
        >
          {mode === 'zone' ? (
            <>
              <div className="mission-context-title">V{(ctxMenu.wpIndex ?? 0) + 1} — נקודת גבול</div>
              <button
                className="mission-context-item"
                style={{ '--action-color': '#f87171' } as React.CSSProperties}
                onClick={() => ctxMenu.wpIndex !== undefined && removeWp(ctxMenu.wpIndex)}
              >
                <span className="mission-context-icon">🗑</span>
                <span>הסר נקודה</span>
              </button>
            </>
          ) : (
            <>
              <div className="mission-context-title">
                {mode === 'waypoints' && ctxMenu.wpIndex !== undefined
                  ? `WP${ctxMenu.wpIndex + 1} — בחר פעולה`
                  : 'בחר פעולת משימה'}
              </div>
              {ACTION_ITEMS.map(item => (
                <button
                  key={item.type}
                  className="mission-context-item"
                  onClick={() => pickAction(item.type)}
                  style={{ '--action-color': item.color } as React.CSSProperties}
                >
                  <span className="mission-context-icon">{item.icon}</span>
                  <span>{item.label}</span>
                </button>
              ))}
              {mode === 'waypoints' && ctxMenu.wpIndex !== undefined && (
                <>
                  <div className="mission-context-sep" />
                  <button
                    className="mission-context-item"
                    style={{ '--action-color': '#94a3b8' } as React.CSSProperties}
                    onClick={e => {
                      e.stopPropagation()
                      setAltVal(String(waypoints[ctxMenu.wpIndex!]?.altitude ?? 50))
                      setAltPopup({ index: ctxMenu.wpIndex! })
                      setCtxMenu(null)
                    }}
                  >
                    <span className="mission-context-icon">↕</span>
                    <span>הגדר גובה</span>
                  </button>
                  <button
                    className="mission-context-item"
                    style={{ '--action-color': '#f87171' } as React.CSSProperties}
                    onClick={() => removeWp(ctxMenu.wpIndex!)}
                  >
                    <span className="mission-context-icon">🗑</span>
                    <span>הסר נקודה</span>
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Altitude popup (waypoints mode) */}
      {altPopup && (
        <div className="mission-alt-popup" onClick={e => e.stopPropagation()}>
          <div className="mission-alt-label">WP{altPopup.index + 1} — גובה (מטרים)</div>
          <input
            type="number"
            className="mission-alt-input"
            value={altVal}
            onChange={e => setAltVal(e.target.value)}
            min={0} max={600} step={5}
            autoFocus
            onKeyDown={e => {
              if (e.key === 'Enter')  confirmAlt()
              if (e.key === 'Escape') setAltPopup(null)
            }}
          />
          <div className="mission-alt-btns">
            <button className="mission-alt-ok"  onClick={confirmAlt}>אישור</button>
            <button className="mission-alt-can" onClick={() => setAltPopup(null)}>ביטול</button>
          </div>
        </div>
      )}

      {/* ── Bottom strip — single mode */}
      {mode === 'single' && selectedPt && (
        <div className="mission-map-summary">
          <span className="mission-map-coords" dir="ltr">
            📍 {selectedPt.lat.toFixed(5)}, {selectedPt.lng.toFixed(5)}
          </span>
          {selectedPt.actionType && (
            <span className="mission-map-action">
              {ACTION_ICON[selectedPt.actionType]} {ACTION_LABEL[selectedPt.actionType]}
            </span>
          )}
          {!readOnly && <span className="mission-map-hint">לחץ ימני לשינוי פעולה</span>}
        </div>
      )}

      {/* ── Bottom strip — waypoints mode */}
      {mode === 'waypoints' && waypoints.length > 0 && (
        <div className="mission-map-summary">
          <span className="mission-map-coords">🗺 {waypoints.length} נקודות</span>
          {dist > 0 && (
            <span className="mission-map-dist" dir="ltr">
              📏 {dist >= 1000 ? `${(dist / 1000).toFixed(2)} km` : `${Math.round(dist)} m`}
            </span>
          )}
          {waypoints.some(w => w.action) && (
            <span className="mission-map-action">{waypoints.filter(w => w.action).length} פעולות</span>
          )}
          {waypoints.some(w => w.altitude != null) && (
            <span className="mission-map-alt">↕ {waypoints.filter(w => w.altitude != null).length} גבהים</span>
          )}
          {!readOnly && <span className="mission-map-hint">לחץ ימני על נקודה לעריכה</span>}
        </div>
      )}

      {/* ── Bottom strip — zone mode */}
      {mode === 'zone' && waypoints.length > 0 && (
        <div className="mission-map-summary">
          <span className="mission-map-coords">⬡ {waypoints.length} נקודות גבול</span>
          {waypoints.length >= 3 && area > 0 && (
            <span className="mission-map-dist" dir="ltr">
              📐 {area >= 10000
                ? `${(area / 10000).toFixed(2)} ha`
                : `${Math.round(area)} m²`}
            </span>
          )}
          {zoneType && (
            <span className="mission-map-action">
              {ACTION_ICON[zoneType]} {ACTION_LABEL[zoneType]}
            </span>
          )}
          {waypoints.length < 3
            ? <span className="mission-map-hint" style={{ color: '#f87171' }}>נדרשות לפחות 3 נקודות לאזור</span>
            : !readOnly && <span className="mission-map-hint">לחץ ימני על נקודה להסרה</span>
          }
        </div>
      )}

      {/* ── Overlay hints */}
      {!readOnly && mode === 'single' && !selectedPt && (
        <div className="mission-map-hint-overlay">
          לחץ על המפה לסימון יעד • לחץ ימני לבחירת פעולה
        </div>
      )}
      {!readOnly && mode === 'waypoints' && waypoints.length === 0 && (
        <div className="mission-map-hint-overlay">
          לחץ להוספת נקודות מסלול • לחץ ימני לנקודה עם פעולה
        </div>
      )}
      {!readOnly && mode === 'zone' && waypoints.length === 0 && (
        <div className="mission-map-hint-overlay">
          לחץ על המפה להגדרת גבולות האזור • לחץ ימני להסרת נקודה
        </div>
      )}
    </div>
  )
}
